import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import {
  KeyboardArrowDownRounded,
  MedicationOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  QrCode2Rounded,
  SchoolOutlined,
} from "@mui/icons-material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import "./Ambaregistraion.css";

const AMBA_TABS = [
  { value: "personal", label: "Personal" },
  { value: "test", label: "Test" },
  { value: "treatment", label: "Treatment" },
];

const GENDER_OPTIONS = ["Male", "Female", "Transgender"];

const CASTE_OPTIONS = ["General", "OBC", "SC", "ST", "Others"];

const VILLAGE_OPTIONS = ["Harmony Springs", "Green Valley", "Riverside", "Sunrise Nagar"];

const VENUE_OPTIONS = [
  "Delhi Wellness Centre",
  "Govt Senior Secondary School",
  "Urban PHC - Harmony Springs",
  "Anganwadi Centre",
];

/* Hb gauge is drawn on a 0 - 15 gm/dL scale */
const HB_SCALE_MAX = 15;

const HB_RANGES = [
  { value: "severe", label: "< 7 gm/dL (Severe)", hb: 6.5, status: "Severe", tone: "severe" },
  { value: "moderate", label: "7 - 9.9 gm/dL (Moderate)", hb: 8.5, status: "Moderate", tone: "moderate" },
  { value: "mild", label: "10 - 11.9 gm/dL (Mild)", hb: 11, status: "Mild", tone: "mild" },
  { value: "normal", label: "≥ 12 gm/dL (Normal)", hb: 12, status: "Normal", tone: "normal" },
];

const WEIGHT_RANGE = { min: 2, max: 100, step: 0.01 };

const HEIGHT_RANGE = { min: 30, max: 180, step: 0.01 };

const BMI_CATEGORIES = [
  { key: "under", label: "Under weight", scale: "(18.5)", short: "Under weight", max: 18.5, torso: 13 },
  { key: "normal", label: "Normal weight", scale: "(25)", short: "Normal", max: 25, torso: 17 },
  { key: "over", label: "Over weight", scale: "(30)", short: "Over weight", max: 30, torso: 22 },
  { key: "obese", label: "Obesity", scale: "(30+)", short: "Obese", max: Infinity, torso: 29 },
];

/* Colour bands behind the BMI scale, thin to heavy */
const BMI_BANDS = [1, 2, 3, 4, 5];

const TREATMENT_CARE_LABEL = "Adolescent Care";

const MEDICINE_OPTIONS = [
  "1 IFA Blue tablet per week",
  "1 IFA Red tablet per day for 3 months",
  "2 IFA Red tablets per day for 3 months",
  "Albendazole 400 mg single dose",
  "Refer to higher facility",
];

const INITIAL_FORM = {
  headOfFamily: "",
  guardianAvailable: true,
  primaryMobile: "",
  beneficiaryName: "",
  abhaId: "",
  abhaAddress: "",
  gender: "",
  primaryCaste: "",
  aadhaarNumber: "",
  village: "",
  schoolName: "",
  address: "",
  dateOfBirth: null,

  /* Test tab */
  venue: "",
  hbRange: "",
  weight: 42.01,
  height: 108.51,

  /* Treatment tab */
  giveTreatment: "yes",
  medicine: "",
  followUpDate: null,
  suggestion: "",
};

/* Flat body silhouette used by the BMI scale - torso width drives the build */
const BodyFigure = ({ torso, active }) => {
  const centre = 24;
  const armGap = torso / 2 + 1;

  return (
    <svg
      viewBox="0 0 48 64"
      className={`amba-bmi-figure${active ? " active" : ""}`}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx={centre} cy="8" r="6" />
      <rect
        x={centre - torso / 2}
        y="15"
        width={torso}
        height="27"
        rx={torso / 2.4}
      />
      <rect x={centre - armGap - 5} y="17" width="5" height="18" rx="2.5" />
      <rect x={centre + armGap} y="17" width="5" height="18" rx="2.5" />
      <rect x={centre - 7} y="38" width="6" height="22" rx="3" />
      <rect x={centre + 1} y="38" width="6" height="22" rx="3" />
    </svg>
  );
};

const AmbaRegistration = () => {
  const [activeTab, setActiveTab] = useState("personal");
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const calculatedAge = useMemo(() => {
    const dob = formData.dateOfBirth;

    if (!dob || !dayjs(dob).isValid()) {
      return "";
    }

    const years = dayjs().diff(dayjs(dob), "year");

    return years === 1 ? "1 Year" : `${years} Years`;
  }, [formData.dateOfBirth]);

  const selectedHbRange = useMemo(
    () => HB_RANGES.find((range) => range.value === formData.hbRange) || null,
    [formData.hbRange]
  );

  const hbPercent = selectedHbRange
    ? Math.min((selectedHbRange.hb / HB_SCALE_MAX) * 100, 100)
    : 0;

  const bmi = useMemo(() => {
    const metres = Number(formData.height) / 100;

    if (!metres) {
      return 0;
    }

    return Number(formData.weight) / (metres * metres);
  }, [formData.weight, formData.height]);

  const bmiCategory = useMemo(
    () => BMI_CATEGORIES.find((category) => bmi < category.max) || BMI_CATEGORIES[0],
    [bmi]
  );

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  };

  const handleChange = (field) => (event) => {
    setField(field, event.target.value ?? "");
  };

  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  const handleLaunchScanner = () => {
    // Aadhaar QR scanner will be wired here once the scanner service is available.
    console.log("Launch Aadhaar scanner");
  };

  const validatePersonal = () => {
    const nextErrors = {};

    if (!formData.headOfFamily.trim()) {
      nextErrors.headOfFamily = "Head of family is required";
    }

    if (!formData.primaryMobile.trim()) {
      nextErrors.primaryMobile = "Primary mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.primaryMobile.trim())) {
      nextErrors.primaryMobile = "Enter a valid 10 digit mobile number";
    }

    if (!formData.beneficiaryName.trim()) {
      nextErrors.beneficiaryName = "Beneficiary name is required";
    }

    if (!formData.gender) {
      nextErrors.gender = "Please select gender";
    }

    if (!formData.primaryCaste) {
      nextErrors.primaryCaste = "Please select primary caste";
    }

    if (!formData.village) {
      nextErrors.village = "Please select village";
    }

    if (!formData.dateOfBirth || !dayjs(formData.dateOfBirth).isValid()) {
      nextErrors.dateOfBirth = "Please select date of birth";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSaveNext = () => {
    if (!validatePersonal()) {
      return;
    }

    const payload = {
      ...formData,
      dateOfBirth: dayjs(formData.dateOfBirth).format("DD-MM-YYYY"),
      calculatedAge,
    };

    console.log("AMBA personal details:", payload);
    setActiveTab("test");
  };

  const validateTest = () => {
    const nextErrors = {};

    if (!formData.venue) {
      nextErrors.venue = "Please select the venue of the test";
    }

    if (!formData.hbRange) {
      nextErrors.hbRange = "Please select today's Hb range";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitTest = () => {
    if (!validateTest()) {
      return;
    }

    console.log("AMBA test details:", {
      venue: formData.venue,
      hbRange: formData.hbRange,
      hbValue: selectedHbRange?.hb,
      weight: formData.weight,
      height: formData.height,
      bmi: Number(bmi.toFixed(2)),
      bmiCategory: bmiCategory.short,
    });
  };

  const buildTreatmentPayload = () => ({
    giveTreatment: formData.giveTreatment,
    medicine: formData.medicine,
    followUpDate: formData.followUpDate
      ? dayjs(formData.followUpDate).format("DD-MM-YYYY")
      : "",
    suggestion: formData.suggestion.trim(),
  });

  const validateTreatment = () => {
    const nextErrors = {};

    /* medicine and follow up only matter when treatment is being given */
    if (formData.giveTreatment === "yes") {
      if (!formData.medicine) {
        nextErrors.medicine = "Please select a medicine";
      }

      if (!formData.followUpDate || !dayjs(formData.followUpDate).isValid()) {
        nextErrors.followUpDate = "Please select the next follow up date";
      }
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSaveDraft = () => {
    console.log("AMBA treatment draft:", buildTreatmentPayload());
  };

  const handleSubmitClose = () => {
    if (!validateTreatment()) {
      return;
    }

    console.log("AMBA treatment plan:", buildTreatmentPayload());
  };

  const renderLabel = (label, required = false) => (
    <Typography component="label" className="amba-field-label">
      {label}
      {required && <span className="amba-required">*</span>}
    </Typography>
  );

  const renderError = (field) =>
    errors[field] ? (
      <FormHelperText className="amba-helper-text">{errors[field]}</FormHelperText>
    ) : null;

  const renderSelect = (field, placeholder, options) => {
    /* options accept plain strings or { value, label } pairs */
    const items = options.map((option) =>
      typeof option === "string" ? { value: option, label: option } : option
    );

    return (
      <Select
        displayEmpty
        value={formData[field]}
        onChange={handleChange(field)}
        error={Boolean(errors[field])}
        className="amba-select"
        IconComponent={KeyboardArrowDownRounded}
        MenuProps={{ className: "amba-select-menu" }}
        renderValue={(selected) => {
          if (!selected) {
            return <span className="amba-select-placeholder">{placeholder}</span>;
          }

          const match = items.find((item) => item.value === selected);

          return match ? match.label : selected;
        }}
      >
        {items.map((item) => (
          <MenuItem key={item.value} value={item.value} className="amba-menu-item">
            {item.label}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const renderPersonalTab = () => (
    <Card className="amba-card">
      {/* =========================
          AADHAAR SCAN BANNER
      ========================== */}
      <Box className="amba-scan-banner">
        <Box className="amba-scan-left">
          <Box className="amba-scan-icon">
            <QrCode2Rounded />
          </Box>

          <Box>
            <Typography className="amba-scan-title">Scan Aadhaar Card</Typography>
            <Typography className="amba-scan-subtitle">
              Instantly autofill all the beneficiary demographic details securely
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          className="amba-scan-button"
          onClick={handleLaunchScanner}
        >
          Launch Scanner
        </Button>
      </Box>

      {/* =========================
          PERSONAL DETAILS FORM
      ========================== */}
      <Box className="amba-form-grid">
        <Box className="amba-field">
          {renderLabel("Head of Family", true)}
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Primary name"
            value={formData.headOfFamily}
            onChange={handleChange("headOfFamily")}
            error={Boolean(errors.headOfFamily)}
            className="amba-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlined />
                </InputAdornment>
              ),
            }}
          />
          {renderError("headOfFamily")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Relationship Status")}
          <Box className="amba-checkbox-wrapper">
            <FormControlLabel
              className="amba-checkbox-label"
              control={
                <Checkbox
                  className="amba-checkbox"
                  checked={formData.guardianAvailable}
                  onChange={(event) =>
                    setField("guardianAvailable", event.target.checked)
                  }
                />
              }
              label="Guardian / Parents available"
            />
          </Box>
        </Box>

        <Box className="amba-field">
          {renderLabel("Primary Mobile Number", true)}
          <TextField
            fullWidth
            size="small"
            placeholder="Enter primary mobile number"
            value={formData.primaryMobile}
            onChange={(event) =>
              setField("primaryMobile", event.target.value.replace(/\D/g, "").slice(0, 10))
            }
            error={Boolean(errors.primaryMobile)}
            className="amba-input"
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneOutlined />
                </InputAdornment>
              ),
            }}
          />
          {renderError("primaryMobile")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Beneficiary Name", true)}
          <TextField
            fullWidth
            size="small"
            placeholder="Enter beneficiary name"
            value={formData.beneficiaryName}
            onChange={handleChange("beneficiaryName")}
            error={Boolean(errors.beneficiaryName)}
            className="amba-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlined />
                </InputAdornment>
              ),
            }}
          />
          {renderError("beneficiaryName")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Enter ABHA ID")}
          <TextField
            fullWidth
            size="small"
            placeholder="42-8821-9901-2354"
            value={formData.abhaId}
            onChange={handleChange("abhaId")}
            className="amba-input"
          />
        </Box>

        <Box className="amba-field">
          {renderLabel("Enter ABHA Address")}
          <TextField
            fullWidth
            size="small"
            placeholder="praveen@abdm"
            value={formData.abhaAddress}
            onChange={handleChange("abhaAddress")}
            className="amba-input"
          />
        </Box>

        <Box className="amba-field">
          {renderLabel("Select Gender", true)}
          {renderSelect("gender", "Select gender", GENDER_OPTIONS)}
          {renderError("gender")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Select Primary Caste", true)}
          {renderSelect("primaryCaste", "Select primary caste", CASTE_OPTIONS)}
          {renderError("primaryCaste")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Aadhaar Number")}
          <TextField
            fullWidth
            size="small"
            placeholder="XXXX-XXXX-8910"
            value={formData.aadhaarNumber}
            onChange={(event) =>
              setField("aadhaarNumber", event.target.value.replace(/\D/g, "").slice(0, 12))
            }
            className="amba-input"
            inputProps={{ inputMode: "numeric", maxLength: 12 }}
          />
        </Box>

        <Box className="amba-field">
          {renderLabel("Select Village", true)}
          {renderSelect("village", "Select village", VILLAGE_OPTIONS)}
          {renderError("village")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Enter School Name")}
          <TextField
            fullWidth
            size="small"
            placeholder="Govt Senior Secondary School"
            value={formData.schoolName}
            onChange={handleChange("schoolName")}
            className="amba-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SchoolOutlined />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box className="amba-field">
          {renderLabel("Address")}
          <TextField
            fullWidth
            size="small"
            placeholder="House No 42, Harmony Springs, South Delhi"
            value={formData.address}
            onChange={handleChange("address")}
            className="amba-input"
          />
        </Box>

        <Box className="amba-field">
          {renderLabel("Select Date of Birth", true)}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD-MM-YYYY"
              value={formData.dateOfBirth}
              maxDate={dayjs()}
              onChange={(value) => setField("dateOfBirth", value)}
              slotProps={{
                field: { openPickerButtonPosition: "start" },
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: Boolean(errors.dateOfBirth),
                  className: "amba-input amba-date-input",
                },
              }}
            />
          </LocalizationProvider>
          {renderError("dateOfBirth")}
        </Box>

        <Box className="amba-field">
          {renderLabel("Calculated Age")}
          <TextField
            fullWidth
            size="small"
            placeholder="15 Years"
            value={calculatedAge}
            className="amba-input amba-input-readonly"
            InputProps={{ readOnly: true }}
          />
        </Box>
      </Box>

      {/* =========================
          FOOTER
      ========================== */}
      <Box className="amba-card-footer">
        <Button
          variant="contained"
          className="amba-primary-button"
          onClick={handleSaveNext}
        >
          Save &amp; Next
        </Button>
      </Box>
    </Card>
  );

  const renderTestTab = () => (
    <>
      <Box className="amba-test-grid">
        {/* =========================
            HAEMOGLOBIN PROFILING
        ========================== */}
        <Card className="amba-card">
          <Typography className="amba-card-title">
            Haemoglobin (Hb) Profiling
          </Typography>

          <Box className="amba-field amba-stacked-field">
            {renderLabel("Venue of the test", true)}
            {renderSelect("venue", "Select venue of the test", VENUE_OPTIONS)}
            {renderError("venue")}
          </Box>

          <Box className="amba-field amba-stacked-field">
            {renderLabel("Select today's Hb range", true)}
            {renderSelect("hbRange", "Select today's Hb range", HB_RANGES)}
            {renderError("hbRange")}
          </Box>

          <Box className="amba-hb-panel">
            <Typography className="amba-hb-panel-title">
              Selected exact Hb value
            </Typography>

            <Box className="amba-hb-gauge">
              <CircularProgress
                variant="determinate"
                value={100}
                size={104}
                thickness={4}
                className="amba-hb-gauge-track"
              />
              <CircularProgress
                variant="determinate"
                value={hbPercent}
                size={104}
                thickness={4}
                className={`amba-hb-gauge-value tone-${selectedHbRange?.tone || "empty"}`}
              />

              <Box className="amba-hb-gauge-reading">
                <Typography className="amba-hb-value">
                  {selectedHbRange ? selectedHbRange.hb.toFixed(1) : "--"}
                </Typography>
                <Typography className="amba-hb-unit">gm/dL</Typography>
              </Box>
            </Box>

            {selectedHbRange && (
              <Box className={`amba-hb-chip tone-${selectedHbRange.tone}`}>
                {selectedHbRange.status}
              </Box>
            )}
          </Box>
        </Card>

        {/* =========================
            BMI CALCULATIONS
        ========================== */}
        <Card className="amba-card">
          <Typography className="amba-card-title">
            BMI (Body Mass Indexing) Calculations
            <span className="amba-required">*</span>
          </Typography>

          <Box className="amba-bmi-metric">
            <Box className="amba-bmi-metric-head">
              {renderLabel("Weight in kg", true)}
              <Typography className="amba-bmi-metric-value">
                {Number(formData.weight).toFixed(2)} kg
              </Typography>
            </Box>
            <Slider
              className="amba-slider"
              value={Number(formData.weight)}
              min={WEIGHT_RANGE.min}
              max={WEIGHT_RANGE.max}
              step={WEIGHT_RANGE.step}
              onChange={(event, value) => setField("weight", value)}
              aria-label="Weight in kg"
            />
          </Box>

          <Box className="amba-bmi-metric">
            <Box className="amba-bmi-metric-head">
              {renderLabel("Height in cm", true)}
              <Typography className="amba-bmi-metric-value">
                {Number(formData.height).toFixed(2)} cm
              </Typography>
            </Box>
            <Slider
              className="amba-slider"
              value={Number(formData.height)}
              min={HEIGHT_RANGE.min}
              max={HEIGHT_RANGE.max}
              step={HEIGHT_RANGE.step}
              onChange={(event, value) => setField("height", value)}
              aria-label="Height in cm"
            />
          </Box>

          <Box className="amba-bmi-panel">
            <Box className="amba-bmi-panel-head">
              <Typography className="amba-bmi-panel-title">
                Resulting Category
              </Typography>
              <Typography className={`amba-bmi-result tone-${bmiCategory.key}`}>
                {`${bmi.toFixed(2)} kg/m² (${bmiCategory.short})`}
              </Typography>
            </Box>

            <Box className="amba-bmi-bar">
              {BMI_BANDS.map((band) => (
                <Box key={band} className={`amba-bmi-bar-band band-${band}`} />
              ))}
            </Box>

            <Box className="amba-bmi-scale">
              {BMI_CATEGORIES.map((category) => {
                const isActive = category.key === bmiCategory.key;

                return (
                  <Box
                    key={category.key}
                    className={`amba-bmi-scale-item${isActive ? " active" : ""}`}
                  >
                    <Typography className="amba-bmi-scale-label">
                      {category.label}
                    </Typography>
                    <Typography className="amba-bmi-scale-range">
                      {category.scale}
                    </Typography>
                    <BodyFigure torso={category.torso} active={isActive} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* =========================
          FOOTER
      ========================== */}
      <Card className="amba-card amba-submit-card">
        <Button
          variant="contained"
          className="amba-primary-button"
          onClick={handleSubmitTest}
        >
          Submit
        </Button>
      </Card>
    </>
  );

  const renderTreatmentTab = () => (
    <Card className="amba-card amba-treatment-shell">
      <Box className="amba-treatment-card">
        {/* =========================
            TREATMENT PLAN HEADER
        ========================== */}
        <Box className="amba-treatment-header">
          <Box className="amba-treatment-header-left">
            <MedicationOutlined />
            <Typography className="amba-treatment-header-title">
              Treatment Plan
            </Typography>
          </Box>

          <Box className="amba-treatment-chip">{TREATMENT_CARE_LABEL}</Box>
        </Box>

        {/* =========================
            TREATMENT PLAN FORM
        ========================== */}
        <Box className="amba-treatment-body">
          <Box className="amba-field amba-stacked-field">
            {renderLabel("Want to give treatment?", true)}
            <RadioGroup
              row
              className="amba-radio-group"
              value={formData.giveTreatment}
              onChange={handleChange("giveTreatment")}
            >
              <FormControlLabel
                value="yes"
                label="Yes"
                className="amba-radio-label"
                control={<Radio className="amba-radio" />}
              />
              <FormControlLabel
                value="no"
                label="No"
                className="amba-radio-label"
                control={<Radio className="amba-radio" />}
              />
            </RadioGroup>
          </Box>

          <Box className="amba-field amba-stacked-field">
            {renderLabel("Select medicine", true)}
            {renderSelect("medicine", "Select medicine", MEDICINE_OPTIONS)}
            {renderError("medicine")}
          </Box>

          <Box className="amba-field amba-stacked-field">
            {renderLabel("Next follow up date", true)}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={formData.followUpDate}
                minDate={dayjs()}
                onChange={(value) => setField("followUpDate", value)}
                slotProps={{
                  field: { openPickerButtonPosition: "start" },
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: Boolean(errors.followUpDate),
                    className: "amba-input amba-date-input",
                  },
                }}
              />
            </LocalizationProvider>
            {renderError("followUpDate")}
          </Box>

          <Box className="amba-field">
            {renderLabel("Any Suggestion")}
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="Drink plenty of water and follow up immediately in case of discomfort or nausea."
              value={formData.suggestion}
              onChange={handleChange("suggestion")}
              className="amba-input amba-textarea"
            />
          </Box>

          {/* =========================
              FOOTER
          ========================== */}
          <Box className="amba-treatment-footer">
            <Button
              variant="outlined"
              className="amba-secondary-button"
              onClick={handleSaveDraft}
            >
              Save as Draft
            </Button>

            <Button
              variant="contained"
              className="amba-primary-button"
              onClick={handleSubmitClose}
            >
              Submit &amp; Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );

  return (
    <Box className="amba-page">
      <Typography className="amba-page-title">AMBA</Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className="amba-tabs"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="AMBA registration sections"
      >
        {AMBA_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            className="amba-tab"
            disableRipple
          />
        ))}
      </Tabs>

      {activeTab === "personal" && renderPersonalTab()}
      {activeTab === "test" && renderTestTab()}
      {activeTab === "treatment" && renderTreatmentTab()}
    </Box>
  );
};

export default AmbaRegistration;
